import React, { MouseEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import { allCountries } from 'country-telephone-data';
import { useSnackbar } from 'notistack';
import { Control, Controller, FieldError, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { v4 as uuid_v4 } from 'uuid';
import dayjs from '~/timeInit';
import {
  isFlightEndDateRequired,
  isReasonOtherTextRequired,
} from '~/validation/schemas/customOfferValidationPredicates';
import {
  createCustomOfferFlightSchema,
  createEditableCustomOfferFlightSchema,
  getLegCountRule,
} from '~/validation/schemas/customOffers';

import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { getCurrencyCodes, getRegionByCurrency } from '@luxuryescapes/lib-regions';

import CustomOfferGstWidget from '~/components/Common/CustomOffers/CustomOfferGstWidget';
import {
  generateFlightTermsAndConditions,
  generateLEBTFlightTermsAndConditions,
  getIsChangedFromLeLiveOffer,
  isFreeTextReason,
  placeCustomOfferOrder,
} from '~/components/Common/CustomOffers/utils';
import DateWidget from '~/components/Common/Elements/DateWidget';
import OrderDetailNotes from '~/components/Purchases/OrderDetail/OrderDetailNotes';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';
import { SERVICE_FEE_TYPE } from '~/consts/flight';
import { OPERATIONS_SUPPORT } from '~/consts/notes';
import stores from '~/consts/stores';
import { UTM_MEDIUM } from '~/consts/utm';

import { useGetEmployee } from '~/hooks/apis/business-traveller/useGetEmployee';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useToggleState from '~/hooks/useToggleState';

import { getEmployeeList } from '~/services/BusinessTraveller/BusinessTravellerService';
import OffersService from '~/services/OffersService';
import * as OrdersService from '~/services/OrdersService';
import { addDays, formatDateISO, getDayJs, isBefore, newDate } from '~/services/TimeService';

import { buildServiceFeeOrderItem } from '~/utils/businessTraveller/serviceFee';
import { areAllFlightAirportsGstApplicable } from '~/utils/businessTravellerGstAirports';
import {
  calculateCustomerSellPriceWithFx,
  deriveCustomerCurrency,
  deriveLeEntity,
  fetchSpotAndContractedRates,
} from '~/utils/customOfferFormUtils';

import ImagesForm from '../Forms/ImagesForm';
import PDFUploadField from '../Forms/PDFUploadField';
import { RichTextEditor } from '../Forms/fields';

import CustomOfferBusinessTravellerServiceFeeSection from './CustomOfferBusinessTravellerServiceFeeSection';
import { FlightLegsSection, FlightLegsSectionRef, asFlightLegsForm } from './CustomOfferFlightLegsSection';
import CustomerPaymentsSection from './CustomOfferForm/CustomerPaymentsSection';
import CustomOfferInternalDiscountField from './CustomOfferInternalDiscountField';
import CustomOfferLoyaltyCalculator from './CustomOfferLoyaltyCalculator';
import CustomOfferMerchantFeeField from './CustomOfferMerchantFeeField';
import {
  CustomOfferLinkedPackageOfferField,
  CustomOfferPackageComponentsSection,
} from './CustomOfferPackageAssociations';
import { CustomOfferRequiredFieldsLegend } from './CustomOfferRequiredFieldsLegend';
import { SabreGDSImport } from './CustomOfferSabreGDSImport';
import { TravellersSection, TravellersSectionRef } from './CustomOfferTravellersSection';
import {
  FLIGHT_PNR_HELPER_TEXT,
  FLIGHT_PROVIDER_OPTIONS,
  FLIGHT_REASON_VALUES,
  FLIGHT_TRAVEL_SCOPE_DOMESTIC,
  FLIGHT_TRAVEL_SCOPE_INTERNATIONAL,
  FLIGHT_TRAVEL_SCOPE_OPTIONS,
  LE_ENTITY_AUS,
  LE_ENTITY_INTL,
  OFFER_TYPES,
  POST_PURCHASE_TYPES,
  PURCHASE_TYPE_POST_PURCHASE,
  PURCHASE_TYPE_SALES,
  SALES_ATTRIBUTION_MARGIN_MAX_VALUE,
  getFlightReasonsList,
  getSalesAttributionList,
} from './constants';
import { buildCustomOfferPaymentPayloadV2 } from './customOfferOrderPayload';
import { getOfferTypeSelectOptions, isOfferTypeSelectLocked } from './customOfferPackagePermissions';
import { ConfirmDeleteDialog } from './dialogs/ConfirmDeleteDialog';
import { buildPendingPointsPayload, fetchLoyaltyTier } from './loyaltyUtils';
import { sendManualV2TrackingAlert } from './manualV2TrackingAlert';
import {
  type PaymentFlowV2FieldErrorMap,
  buildCustomerPaymentMetadata,
  calculatePaymentFlowV2DerivedAmounts,
  deriveDueDateFromPaymentFlowV2,
  getCustomerPaymentSectionState,
  hydratePaymentFlowV2FormValues,
} from './paymentFlowUtils';
import { getEmployeeFromSabrePassenger } from './utils/sabre/getEmployeeFromSabrePassenger';
import { SabreParsedData } from './utils/sabre/parseSabreGDS';

import CustomOfferType = App.CustomOfferType;

const phonePrefixOptions: Array<{ value: string; label: string }> = allCountries.map(({ dialCode, name }) => ({
  value: dialCode,
  label: `+${dialCode} ${name}`,
}));

type Props = {
  user: App.User;
  currentOffer?: App.CustomOffer;
  hasOrders?: boolean;
  order?: App.Order;
  onUpdate?: (offer: App.CustomOffer) => Promise<void>;
  paymentDate?: string;
  changeFormType: (type: App.CustomOfferType, internalDiscountTracking?: string | null) => void;
  offerTypes: typeof OFFER_TYPES;
  brand: App.Brands;
  travellersSectionRef: RefObject<TravellersSectionRef>;
  isBusinessTraveller: boolean;
  customerHasPaid: boolean;
};

export const buildDefaultValues = (
  brand: string,
  offer?: App.CustomOffer,
  userCountryCode?: string,
): CustomOfferFormValues => {
  const today = new Date().toISOString().split('T')[0];
  const item = offer?.items[0];
  const travellers = offer?.items[0]?.travellers;
  const isPostPurchaseOffer = offer?.purchase_type === 'post-purchase';
  const isBusinessTravellerOffer = brand === LE_BUSINESS_TRAVELLER;
  const isReturnFlight = item?.end_date ? true : false;
  const flight_terms_and_conditions = item?.flight_terms_and_conditions;

  let reasonDefaultValue: string;
  let reasonOtherDefaultValue: string;

  if (offer?.reason && isFreeTextReason(offer.reason, FLIGHT_REASON_VALUES)) {
    reasonDefaultValue = 'other';
    reasonOtherDefaultValue = offer.reason;
  } else {
    reasonDefaultValue = offer?.reason ?? '';
    reasonOtherDefaultValue = '';
  }

  const defaultPriceCurrency =
    !offer && userCountryCode ? (deriveCustomerCurrency(brand, userCountryCode) ?? 'AUD') : 'AUD';

  const hydratedPaymentFlowV2 = hydratePaymentFlowV2FormValues(offer?.payment_metadata?.payment_flow_v2, !offer);
  if (!hydratedPaymentFlowV2.type) {
    hydratedPaymentFlowV2.type = 'full_payment';
  }
  const legacyDueDate = offer?.due_date ? formatDateISO(offer.due_date) : undefined;
  if (!hydratedPaymentFlowV2.payment_deadline && legacyDueDate) {
    hydratedPaymentFlowV2.payment_deadline = legacyDueDate;
  }

  return {
    item: {
      type: 'flight',
      name: item?.name ?? '',
      description: item?.description ?? '',
      vendor: item?.vendor ?? '',
      provider: FLIGHT_PROVIDER_OPTIONS.some((providerOption) => providerOption.value === item?.provider)
        ? item?.provider
        : '',
      vendor_reference_number: item?.vendor_reference_number ?? '',
      salesforce_case_number: item?.salesforce_case_number ?? '',
      flight_terms_and_conditions: {
        currency: flight_terms_and_conditions?.currency ?? 'AUD',
        change_fee: flight_terms_and_conditions?.change_fee ?? 0,
        is_changeable: flight_terms_and_conditions?.is_changeable ?? false,
        cancellation_fee: flight_terms_and_conditions?.cancellation_fee ?? 0,
        is_cancellable: flight_terms_and_conditions?.is_cancellable ?? false,
        le_currency: flight_terms_and_conditions?.le_currency ?? 'AUD',
        le_change_fee: flight_terms_and_conditions?.le_change_fee ?? 0,
        le_is_changeable: flight_terms_and_conditions?.le_is_changeable ?? false,
        le_cancellation_fee: flight_terms_and_conditions?.le_cancellation_fee ?? 0,
        le_is_cancellable: flight_terms_and_conditions?.le_is_cancellable ?? false,
      },
      service_information: item?.service_information ?? '',
      start_date: item?.start_date ?? today,
      end_date: isReturnFlight ? (item?.end_date ?? formatDateISO(addDays(1))) : undefined,
      flight_type: item?.flight_type ?? (isReturnFlight ? 'return' : 'one-way'),
      travel_scope:
        item?.travel_scope === FLIGHT_TRAVEL_SCOPE_DOMESTIC || item?.travel_scope === FLIGHT_TRAVEL_SCOPE_INTERNATIONAL
          ? item.travel_scope
          : undefined,
      flight_legs: item?.flight_legs ?? [],
    },
    travellers:
      travellers?.length > 0
        ? travellers
        : [
            {
              first_name: '',
              middle_name: '',
              last_name: '',
              date_of_birth: today,
              type: 'adult',
              ...(isPostPurchaseOffer ? { airline_fee: 0, le_fee: 0 } : { fare_and_taxes: 0 }),
            },
          ],
    price: offer?.price ? Number(offer.price) / 100 : 0,
    price_currency: offer?.price_currency ?? defaultPriceCurrency,
    cost_price: offer?.cost_price ? Number(offer.cost_price) / 100 : 0,
    cost_price_currency: offer?.cost_price_currency ?? 'AUD',
    due_date: deriveDueDateFromPaymentFlowV2(hydratedPaymentFlowV2, legacyDueDate),
    sold_at: today,
    payment_terms: 'eft',
    images: offer?.images ?? [],
    customer_phone_prefix: offer?.customer_phone_prefix ?? '',
    customer_phone_number: offer?.customer_phone_number ?? '',
    payment_metadata: {
      margin: offer?.payment_metadata?.margin ? offer.payment_metadata.margin / 100 : 0,
      le_service_fee: brand === LE_BUSINESS_TRAVELLER ? 0 : (offer?.payment_metadata?.le_service_fee ?? 0) / 100,
      service_fee_type: offer?.payment_metadata?.service_fee_type ?? 'No fee',
      lebt_service_fee: offer?.payment_metadata?.lebt_service_fee,
      payment_type: 'full_payment',
      customer_exchange_rate: offer?.payment_metadata?.customer_exchange_rate ?? 1,
      customer_deposit_1_amount: 0,
      customer_deposit_1_due_date: null,
      deposit_pi_number_customer: null,
      final_amount_customer: offer?.price ? Number(offer.price) / 100 : 0,
      payment_flow_v2: hydratedPaymentFlowV2,
      block_customer_payment_notifications: offer?.payment_metadata?.block_customer_payment_notifications ?? false,
      merchant_fee_enabled:
        isBusinessTravellerOffer || !isPostPurchaseOffer
          ? (offer?.payment_metadata?.merchant_fee_enabled ?? true)
          : false,
      vendor_payment_type: 'full_payment',
      vendor_exchange_rate: offer?.payment_metadata?.vendor_exchange_rate ?? 1,
      vendor_due_date: null,
      vendor_was_paid: false,
      vendor_deposit_1_amount: null,
      vendor_deposit_1_due_date: null,
      final_payment_vcc_digits: null,
      deposit_vcc_digits: null,
      final_amount_vendor: offer?.cost_price ? Number(offer.cost_price) / 100 : 0,
      vendor_payment_date: null,
      vendor_deposit_payment_date: null,
      le_entity: offer?.payment_metadata?.le_entity ?? deriveLeEntity(defaultPriceCurrency),
      sales_attribution: offer?.payment_metadata?.sales_attribution ?? UTM_MEDIUM,
      created_by: offer?.payment_metadata?.created_by ?? '',
      sales_agent_name: offer?.payment_metadata?.sales_agent_name ?? '',
      internal_discount_tracking: offer?.payment_metadata?.internal_discount_tracking ?? '',
      price_includes_gst: offer?.payment_metadata?.price_includes_gst ?? false,
      gst_amount: (offer?.payment_metadata?.gst_amount ?? 0) / 100,
    },
    post_purchase_type: offer?.post_purchase_type ?? '',
    notes: offer?.notes ?? '',
    created_from_item_id: offer?.created_from_item_id ?? null,
    fk_package_custom_offer_id: offer?.fk_package_custom_offer_id ?? null,
    reason: reasonDefaultValue,
    reasonOther: reasonOtherDefaultValue,
    supplier: 'Other',
    hiddenFromCustomer: offer?.hidden_from_customer ?? false,
    pdf_filename: offer?.pdf_filename ?? null,
    pdf_s3_key: offer?.pdf_s3_key ?? null,
    pdf_download_url: offer?.pdf_download_url ?? null,
  };
};

type Payload = Omit<App.CustomOffer, 'id' | 'status' | 'has_vcc' | 'created_at' | 'updated_at'> & {
  pdf?: {
    filename: string;
    content: string;
    mimeType: string;
  };
};

export const buildPayload = (
  user: App.User,
  formData: CustomOfferFormValues,
  images,
  brand: string,
  purchaseType: 'sales' | 'post-purchase',
  postPurchaseType?: string,
): Payload => {
  const { item, travellers, payment_metadata, reason, reasonOther, pdf, hiddenFromCustomer, ...rest } = formData;

  const gstAmountInCents = Math.round((payment_metadata?.gst_amount ?? 0) * 100);
  const shouldAllowMerchantFees = brand === LE_BUSINESS_TRAVELLER || purchaseType === PURCHASE_TYPE_SALES;
  const customerPaymentMetadata = buildCustomerPaymentMetadata(payment_metadata);
  const payload: Payload = {
    ...rest,
    hidden_from_customer: hiddenFromCustomer,
    purchase_type: purchaseType,
    post_purchase_type: purchaseType === 'post-purchase' ? postPurchaseType : undefined,
    price: Math.round(rest.price * 100),
    cost_price: Math.round(rest.cost_price * 100),
    due_date: deriveDueDateFromPaymentFlowV2(
      payment_metadata.payment_flow_v2,
      rest.due_date != null && rest.due_date !== '' ? formatDateISO(rest.due_date) : undefined,
    ),
    sold_at: formatDateISO(rest.sold_at),
    payment_metadata: {
      ...payment_metadata,
      vendor_due_date: null,
      ...customerPaymentMetadata,
      merchant_fee_enabled: shouldAllowMerchantFees ? (payment_metadata.merchant_fee_enabled ?? true) : false,
      final_amount_customer: Math.round(rest.price * 100),
      final_amount_vendor: Math.round(rest.cost_price * 100),
      le_service_fee: Math.round(payment_metadata.le_service_fee * 100),
      margin: Math.round(payment_metadata.margin * 100),
      // LEBT service fee, amount is in dollars.
      ...(payment_metadata.lebt_service_fee && { lebt_service_fee: payment_metadata.lebt_service_fee }),
      le_entity: payment_metadata?.le_entity,
      additional_vendor_payment: payment_metadata.additional_vendor_payment
        ? Math.round(payment_metadata.additional_vendor_payment * 100)
        : 0,
      additional_margin:
        payment_metadata.additional_margin != null ? Math.round(payment_metadata.additional_margin * 100) : null,
      internal_discount_tracking: payment_metadata.internal_discount_tracking?.trim()
        ? payment_metadata.internal_discount_tracking.trim()
        : null,
      reason_additional_info: payment_metadata?.reason_additional_info,
      reason_issue_raised: payment_metadata?.reason_issue_raised,
      price_includes_gst: payment_metadata?.price_includes_gst ?? false,
      gst_amount: gstAmountInCents,
    },
    name: item.name,
    fk_customer_id: user.id_member,
    customer_full_name: user.fullName,
    images: images,
    items: [
      {
        ...item,
        flight_type: item.flight_type,
        travel_scope: purchaseType === PURCHASE_TYPE_SALES ? item.travel_scope : undefined,
        start_date: formatDateISO(item.start_date),
        end_date: item.end_date ? formatDateISO(item.end_date) : undefined,
        travellers: travellers.map((traveller) => ({
          ...traveller,
          date_of_birth: traveller.date_of_birth ? formatDateISO(traveller.date_of_birth) : null,
        })),
        gst_amount: gstAmountInCents / 100,
        flight_legs: item.flight_legs?.map((leg) => ({
          ...leg,
          segments: leg.segments.map((segment) => ({
            ...segment,
            departureDate: segment.departureDate ? formatDateISO(segment.departureDate) : '',
            arrivalDate: segment.arrivalDate ? formatDateISO(segment.arrivalDate) : '',
          })),
        })),
      },
    ],
    ...(pdf?.content && { pdf: { filename: pdf.filename, content: pdf.content, mimeType: pdf.mimeType } }),
  };

  if (reason && reason === 'other' && reasonOther) {
    payload.reason = reasonOther;
  } else {
    payload.reason = reason ?? undefined;
  }

  return payload;
};

const buildPaymentPayload = async (user: App.User, offer: App.CustomOffer, brand: App.Brands = 'luxuryescapes') => {
  const transactionKey = uuid_v4();
  const total = offer.payment_metadata.final_amount_customer / 100;
  const gstAmount = offer.payment_metadata.gst_amount ? offer.payment_metadata.gst_amount / 100 : 0;
  const regionCode = getRegionByCurrency(offer.price_currency, brand);

  const pendingPoints = await buildPendingPointsPayload(
    user,
    offer,
    transactionKey,
    offer.payment_metadata?.sales_attribution,
    brand,
  );
  const tier = pendingPoints ? await fetchLoyaltyTier(user.id_member) : null;

  // LEBT appends service_fee_item to the order payload
  const serviceFeeItem = brand === LE_BUSINESS_TRAVELLER ? buildServiceFeeOrderItem(offer.payment_metadata) : null;

  return {
    brand,
    currency_code: offer.price_currency,
    region_code: regionCode.code,
    customer_id: user.id_member,
    customer_email: user.email,
    customer_phone_prefix: offer.customer_phone_prefix,
    customer_phone: offer.customer_phone_number,
    utm_source: brand,
    utm_medium: offer?.payment_metadata?.sales_attribution ?? UTM_MEDIUM,
    transaction_key: transactionKey,
    type: 'custom_offer',
    ...(brand === LE_BUSINESS_TRAVELLER && {
      concierge_mode: offer.payment_metadata.lebt_service_fee?.concierge_mode ?? false,
    }),
    items: [
      ...offer.items.map((item) => ({
        ...item,
        transaction_key: transactionKey,
        type: 'custom_offer' as unknown as CustomOfferType,
        custom_offer_id: offer.id,
        due_date: offer.due_date,
        total,
        custom_item_type: item.type,
        gst_amount: gstAmount,
      })),
      ...(serviceFeeItem ? [serviceFeeItem] : []),
    ],
    ...(pendingPoints && tier && { lux_loyalty: { pendingPoints, tier } }),
  };
};

const getPostPurchaseTypeLabel = (postPurchaseType?: string) => {
  return POST_PURCHASE_TYPES.find((type) => type.value === postPurchaseType)?.label.toLowerCase() ?? '';
};

const stripPostPurchaseSuffix = (name: string): string => {
  const trimmed = name.trim();
  for (const { label } of POST_PURCHASE_TYPES) {
    const suffix = ` (${label.toLowerCase()})`;
    if (trimmed.endsWith(suffix)) {
      return trimmed.slice(0, -suffix.length).trim();
    }
  }
  return trimmed;
};

export const buildPostPurchaseOfferName = (baseOfferName: string, postPurchaseType?: string) => {
  const trimmedBaseOfferName = baseOfferName.trim();

  if (!trimmedBaseOfferName) {
    return '';
  }

  const postPurchaseTypeLabel = getPostPurchaseTypeLabel(postPurchaseType);

  return postPurchaseTypeLabel ? `${trimmedBaseOfferName} (${postPurchaseTypeLabel})` : trimmedBaseOfferName;
};

export const getBookingJourneyOfferName = (bookingLegs: Array<App.BookingJourneyLeg> = []) => {
  const firstLeg = bookingLegs[0];
  const firstLegFlights = firstLeg?.flights ?? [];

  const firstSegment = firstLegFlights[0];
  const lastSegment = firstLegFlights[firstLegFlights.length - 1];

  const departureLocation =
    firstLeg?.departureCity ||
    firstLeg?.departureAirport ||
    firstSegment?.departureCity ||
    firstSegment?.departureAirport;

  const departureArrivalLocation =
    firstLeg?.arrivalCity || firstLeg?.arrivalAirport || lastSegment?.arrivalCity || lastSegment?.arrivalAirport;

  if (!departureLocation || !departureArrivalLocation) {
    return '';
  }

  return `${departureLocation} → ${departureArrivalLocation}`;
};

export default function CustomOfferFlightForm({
  user,
  hasOrders,
  order,
  onUpdate,
  currentOffer,
  changeFormType,
  offerTypes,
  brand,
  travellersSectionRef,
  isBusinessTraveller,
  customerHasPaid,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentAgent } = useCurrentUser();

  const [offer, setOffer] = useState<App.CustomOffer | undefined>(currentOffer);
  const [inclusionsDiscussed, setInclusionsDiscussed] = useState(offer?.status === 'approved');
  const [termsExplained, setTermsExplained] = useState(offer?.status === 'approved');
  const [sendStatus, setSendStatus] = useState<Utils.ProcessingState>('idle');
  const [images, setImages] = useState([]);
  const [formattedImages, setFormattedImages] = useState(offer?.images ?? []);

  // This is a dirty fix for the fact only save the date to the DB, but need to take timestamp on client for terms and conditions generation
  if (currentOffer?.due_date) {
    currentOffer.due_date = getDayJs(currentOffer.due_date).hour(23).minute(59).toISOString();
  }

  const { isToggled: deleteDialogOpen, toggleOn: openDeleteDialog, toggleOff: closeDeleteDialog } = useToggleState();

  // Post-purchase custom offer states
  const [purchaseType, setPurchaseType] = useState<'sales' | 'post-purchase'>(offer?.purchase_type ?? 'sales');
  const [postPurchaseType, setPostPurchaseType] = useState<string>(offer?.post_purchase_type ?? '');
  const [pnrValue, setPnrValue] = useState<string>(offer?.items[0]?.vendor_reference_number ?? '');
  const [pnrSearching, setPnrSearching] = useState(false);

  const isSalesMode = purchaseType === PURCHASE_TYPE_SALES;

  const { data: employee } = useGetEmployee(user.id_member, isBusinessTraveller && !!user.id_member);

  const { data: employeeListResponse } = useQuery({
    queryKey: ['employeeList', employee?.businessId],
    queryFn: () => getEmployeeList(employee?.businessId, { page: 0, pageSize: 1000 }),
    enabled: isBusinessTraveller && !!employee?.businessId,
  });

  const businessEmployees = useMemo(
    () =>
      (employeeListResponse?.result ?? [])
        .filter((employee) => employee.status === 'ACTIVATED')
        .sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        }),
    [employeeListResponse],
  );

  // We want all currencies to show for airline currencies so we don't pass a brand
  const airlineCurrencies = useMemo(() => getCurrencyCodes().sort((a, b) => a.localeCompare(b)), []);
  const customerCurrencies = useMemo(() => getCurrencyCodes(brand).sort((a, b) => a.localeCompare(b)), [brand]);

  const expectedCustomerCurrency = useMemo(() => {
    if (!user.country_code) {
      return null;
    }
    return deriveCustomerCurrency(brand, user.country_code);
  }, [user.country_code, brand]);

  const { data: fxRates } = useQuery({
    queryKey: ['customOfferFxRates', customerCurrencies],
    queryFn: async () => {
      return await fetchSpotAndContractedRates(customerCurrencies, enqueueSnackbar);
    },
    enabled: customerCurrencies.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const customerFxRates = fxRates?.customerFxRates;
  const contractFxRates = fxRates?.contractFxRates;

  const costPriceCurrencies = useMemo(() => {
    if (contractFxRates) {
      return Object.keys(contractFxRates);
    }

    return [];
  }, [contractFxRates]);

  useEffect(() => {
    setImages(
      offer?.images
        ? offer?.images.map((id) => ({
            id: id,
            publicImageId: id,
          }))
        : [],
    );
  }, [offer]);

  const { push, replace } = useAppNavigation();

  const isEditMode = !!offer?.id;

  const activePurchaseType = isSalesMode ? 'sales' : purchaseType;

  const validationSchema = useMemo(
    () =>
      hasOrders
        ? createEditableCustomOfferFlightSchema(brand, activePurchaseType)
        : createCustomOfferFlightSchema(brand, activePurchaseType),
    [hasOrders, brand, activePurchaseType],
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    watch,
    getValues,
    clearErrors,
    setValue,
  } = useForm<CustomOfferFormValues>({
    defaultValues: buildDefaultValues(brand, offer, user.country_code),
    context: { editMode: isEditMode, brand },
    resolver: yupResolver(validationSchema as unknown as Parameters<typeof yupResolver>[0]),
    reValidateMode: 'onChange',
  });

  const flightLegsSectionRef = useRef<FlightLegsSectionRef>(null);

  const watchedTravellers = useWatch({ control, name: 'travellers' });
  const watchedMargin = useWatch({ control, name: 'payment_metadata.margin' });
  const watchedServiceFee = useWatch({ control, name: 'payment_metadata.le_service_fee' });
  const watchedSellPrice = useWatch({ control, name: 'price' });
  const itemType = watch('item.type');
  const priceCurrencyCode = watch('price_currency');
  const costPriceCurrencyCode = watch('cost_price_currency');
  const salesAttribution = watch('payment_metadata.sales_attribution');
  const reason = watch('reason');
  const flightType = watch('item.flight_type') || 'one-way';
  const endDate = watch('item.end_date');
  const flightTerms = useWatch({ control, name: 'item.flight_terms_and_conditions' });
  const flightLegs = watch('item.flight_legs');
  const priceIncludesGst = watch('payment_metadata.price_includes_gst');
  const dueDateValue = useWatch({ control, name: 'due_date' });
  const vendorExchangeRate = watch('payment_metadata.vendor_exchange_rate');
  const customerExchangeRate = watch('payment_metadata.customer_exchange_rate');
  const isCustomOfferPackage = itemType === 'custom-offer-package';
  const offerTypeSelectOptions = useMemo(
    () => getOfferTypeSelectOptions(offerTypes, currentAgent, itemType),
    [offerTypes, currentAgent, itemType],
  );
  const isOfferTypeSelectDisabled = isSubmitting || isOfferTypeSelectLocked(currentAgent, itemType);
  const linkedPackageOfferId = watch('fk_package_custom_offer_id');
  const hiddenFromCustomer = watch('hiddenFromCustomer');
  const canUseFlightDepositPayment = Boolean(linkedPackageOfferId) && hiddenFromCustomer;
  const blockCustomerPaymentNotifications = watch('payment_metadata.block_customer_payment_notifications');
  const depositFlow = watch('payment_metadata.payment_flow_v2.type');
  const showFlightManualDepositOption = canUseFlightDepositPayment || depositFlow === 'manual';
  const manualV2DepositAmount = watch('payment_metadata.payment_flow_v2.manual_deposit_amount');
  const paymentDeadline = watch('payment_metadata.payment_flow_v2.payment_deadline');
  const manualBalanceDueDate = watch('payment_metadata.payment_flow_v2.manual_balance_due_date');
  const paymentFlowV2Errors = errors.payment_metadata?.payment_flow_v2 as PaymentFlowV2FieldErrorMap | undefined;
  const customerPaymentSectionState = useMemo(
    () =>
      getCustomerPaymentSectionState({
        isExistingOffer: isEditMode,
        paymentFlowType: depositFlow,
        legacyPaymentType: 'full_payment',
        isCustomOfferPackage: false,
      }),
    [depositFlow, isEditMode],
  );
  const isFlightManualDepositFlow = depositFlow === 'manual';
  const flightDepositPaymentFlowHelperText =
    isFlightManualDepositFlow && !canUseFlightDepositPayment
      ? 'Re-link to a package and hide from the customer portal, or switch to full payment before saving.'
      : !canUseFlightDepositPayment
        ? 'Manual deposit is available when the flight is hidden from the customer portal and linked to a package offer.'
        : undefined;

  // Sync the customer exchange rate once FX rates load for auto-derived non-AUD currencies on new offers
  useEffect(() => {
    if (!isEditMode && customerFxRates && priceCurrencyCode && priceCurrencyCode !== 'AUD') {
      const rate = customerFxRates[priceCurrencyCode];
      if (rate) {
        setValue('payment_metadata.customer_exchange_rate', rate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFxRates]);

  useEffect(() => {
    if (isCustomOfferPackage) {
      setValue('fk_package_custom_offer_id', null);
    }
  }, [isCustomOfferPackage, setValue]);

  useEffect(() => {
    if (depositFlow === 'payment_schedule') {
      setValue('payment_metadata.payment_flow_v2.type', 'full_payment');
    }
  }, [depositFlow, setValue]);

  useEffect(() => {
    if (!isBusinessTraveller && !isSalesMode) {
      setValue('payment_metadata.merchant_fee_enabled', false);
    }
  }, [isBusinessTraveller, isSalesMode, setValue]);

  // Package component flights with deposit: default to suppressing payment notification emails.
  useEffect(() => {
    if (canUseFlightDepositPayment && isFlightManualDepositFlow && !blockCustomerPaymentNotifications) {
      setValue('payment_metadata.block_customer_payment_notifications', true);
    }
  }, [blockCustomerPaymentNotifications, canUseFlightDepositPayment, depositFlow, isFlightManualDepositFlow, setValue]);

  const computedCostPrice = useMemo(() => {
    const travellers = watchedTravellers || [];
    if (!isSalesMode) {
      return travellers.reduce((total, t) => total + (parseFloat(String(t.airline_fee)) || 0), 0);
    }
    return travellers.reduce((total, t) => total + (parseFloat(String(t.fare_and_taxes)) || 0), 0);
  }, [watchedTravellers, isSalesMode]);

  const computedLeFeeTotal = useMemo(() => {
    if (isSalesMode) {
      return 0;
    }
    const travellers = watchedTravellers || [];
    return travellers.reduce((total, t) => total + (parseFloat(String(t.le_fee)) || 0), 0);
  }, [watchedTravellers, isSalesMode]);

  const computedSellPrice = useMemo(() => {
    if (!isSalesMode) {
      return calculateCustomerSellPriceWithFx({
        costPrice: computedCostPrice,
        serviceFee: computedLeFeeTotal,
        customerExchangeRate,
        vendorExchangeRate,
      });
    }
    return calculateCustomerSellPriceWithFx({
      costPrice: computedCostPrice,
      margin: Number(watchedMargin) || 0,
      serviceFee: Number(watchedServiceFee) || 0,
      customerExchangeRate,
      vendorExchangeRate,
    });
  }, [
    computedCostPrice,
    computedLeFeeTotal,
    customerExchangeRate,
    isSalesMode,
    vendorExchangeRate,
    watchedMargin,
    watchedServiceFee,
  ]);

  const isActionsDisabled = isSubmitting || sendStatus == 'processing';
  const isSendDisabled = !inclusionsDiscussed || !termsExplained || isActionsDisabled;
  const isAdditionalMarginVisible = salesAttribution === SALES_ATTRIBUTION_MARGIN_MAX_VALUE;
  const shouldLockLeLiveAttribution =
    isEditMode && customerHasPaid && offer?.payment_metadata?.sales_attribution === stores.le_live.utmMedium;

  const showSendButton = !!offer?.id && !hasOrders;

  const isChangedFromLeLiveOffer = getIsChangedFromLeLiveOffer({
    offer,
    nextItemType: itemType,
  });

  /** LE LIVE has no real vendor split; once switched away during edit, allow vendor pricing edits with an order. */
  const lockVendorPriceFieldsForPaidOrder = hasOrders && !isChangedFromLeLiveOffer;

  const onSubmit: SubmitHandler<CustomOfferFormValues> = useCallback(
    async (formData) => {
      try {
        const payload = buildPayload(
          user,
          {
            ...formData,
            price: computedSellPrice,
            cost_price: computedCostPrice,
          },
          formattedImages,
          brand,
          purchaseType,
          postPurchaseType,
        );
        payload.is_changed_from_le_live_offer = isChangedFromLeLiveOffer;
        if (shouldLockLeLiveAttribution) {
          payload.payment_metadata.sales_attribution = stores.le_live.utmMedium;
        }

        const response = offer?.id
          ? await OffersService.updateCustomOffer(offer.id, payload)
          : await OffersService.createCustomOffer(payload);

        const updatedOffer = response.result as App.CustomOffer;

        setOffer(updatedOffer);

        if (onUpdate) {
          await onUpdate(updatedOffer);
        }

        enqueueSnackbar(
          offer?.id ? 'Custom quote has been successfully updated' : 'Custom quote has been successfully created',
          { variant: 'success' },
        );

        replace(`/users/${user.id_member}/custom-offers/${updatedOffer.id}`);
      } catch (err) {
        enqueueSnackbar(`Can't process custom quote: ${err.message}`, { variant: 'error' });
      }
    },
    [
      user,
      formattedImages,
      isChangedFromLeLiveOffer,
      shouldLockLeLiveAttribution,
      offer,
      onUpdate,
      enqueueSnackbar,
      replace,
      brand,
      purchaseType,
      postPurchaseType,
      computedSellPrice,
      computedCostPrice,
    ],
  );

  const onSendPayment = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!offer?.id) {
      return;
    }

    try {
      setSendStatus('processing');

      const dueDate = offer.due_date;

      if (isBefore(addDays(1, dueDate))) {
        throw new Error('Due date must same day or in the future');
      }

      const offerResponse = await OffersService.setCustomOfferStatus(offer.id, 'approved');
      const updatedOffer = offerResponse.result as App.CustomOffer;

      setOffer(updatedOffer);

      const isV2Flow = updatedOffer.payment_metadata?.payment_flow_version === 'v2';
      const payload = isV2Flow
        ? await buildCustomOfferPaymentPayloadV2(user, updatedOffer, { brand, supportManualFlow: true })
        : await buildPaymentPayload(user, updatedOffer, brand);

      const response = await placeCustomOfferOrder(isBusinessTraveller, user, payload);

      if (isV2Flow && updatedOffer.payment_metadata?.payment_flow_v2?.type === 'manual') {
        void sendManualV2TrackingAlert({
          agentEmail: currentAgent?.email ?? 'unknown',
          orderId: response.result.id_orders,
        });
      }

      if (offer.notes && offer.notes.length > 0) {
        await OrdersService.addOrderNote(response.result.id_orders, {
          comment: offer.notes,
          comment_type: OPERATIONS_SUPPORT,
        });
      }

      enqueueSnackbar('Payment request has been successfully created', { variant: 'success' });
      setSendStatus('success');

      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      setSendStatus('failed');
      enqueueSnackbar(`Can't create payment request: ${err.message}`, { variant: 'error' });
    }
  };

  const onUpdateImages = (newImageList) => {
    const mappedImages = newImageList.map((image) => image.id);
    return setFormattedImages([...mappedImages]);
  };

  const onAddImage = (cloudinaryId, filename) => {
    const newImage = { id: cloudinaryId, publicImageId: cloudinaryId, title: filename };
    setImages([newImage, ...images]);
    return Promise.resolve(newImage);
  };

  const onDeleteImage = async (imageId) => {
    setImages((images) => images.filter((image) => image.id !== imageId));
    return;
  };

  const deleteOffer = async () => {
    OffersService.deleteCustomOffer(offer.id).then(() => push(`/users/${user.id_member}`));
  };

  // Update computed prices in form state
  useEffect(() => {
    setValue('cost_price', computedCostPrice);
    setValue('price', computedSellPrice);
    clearErrors(['cost_price', 'price']);
  }, [computedCostPrice, computedSellPrice, setValue, clearErrors]);

  const derivedPaymentFlowV2Amounts = useMemo(
    () =>
      calculatePaymentFlowV2DerivedAmounts({
        paymentFlowType: depositFlow,
        price: watchedSellPrice,
        depositAmount: 0,
        manualDepositAmount: manualV2DepositAmount,
      }),
    [depositFlow, manualV2DepositAmount, watchedSellPrice],
  );

  useEffect(() => {
    if (depositFlow === 'manual') {
      setValue(
        'payment_metadata.payment_flow_v2.manual_balance_amount',
        derivedPaymentFlowV2Amounts.manual_balance_amount,
      );
      clearErrors('payment_metadata.payment_flow_v2.manual_balance_amount');
      return;
    }

    if (depositFlow === 'full_payment') {
      setValue('payment_metadata.payment_flow_v2.balance_amount', derivedPaymentFlowV2Amounts.balance_amount);
      clearErrors('payment_metadata.payment_flow_v2.balance_amount');
    }
  }, [clearErrors, depositFlow, derivedPaymentFlowV2Amounts, setValue]);

  useEffect(() => {
    const v2 = getValues('payment_metadata.payment_flow_v2');
    const raw = getValues('due_date');
    const formDue = raw != null && raw !== '' ? formatDateISO(raw) : undefined;
    const offerDue = offer?.due_date ? formatDateISO(offer.due_date) : undefined;
    const next = deriveDueDateFromPaymentFlowV2(v2, formDue ?? offerDue);
    if ((formDue ?? '') !== next) {
      setValue('due_date', next, { shouldValidate: true });
    }
  }, [depositFlow, getValues, manualBalanceDueDate, offer?.due_date, paymentDeadline, setValue]);

  // Handle flight type changes - clear end_date when switching to one-way
  useEffect(() => {
    if (flightType === 'one-way' && endDate) {
      setValue('item.end_date', undefined);
      clearErrors(['item.end_date']);
    }
  }, [flightType, endDate, setValue, clearErrors]);

  // Calculate GST amount based on price and checkbox
  useEffect(() => {
    const gstAmount = priceIncludesGst ? (Number(computedSellPrice) || 0) / 11 : 0;
    setValue('payment_metadata.gst_amount', gstAmount);
    clearErrors('payment_metadata.gst_amount');
  }, [priceIncludesGst, computedSellPrice, setValue, clearErrors]);

  // Serialize segment airport codes so the effect runs when user types (watch() can return same reference on nested updates)
  const flightLegsAirportKey =
    JSON.stringify(
      flightLegs?.map(
        (leg) => leg.segments?.map((s) => ({ d: s.departureAirport ?? '', a: s.arrivalAirport ?? '' })) ?? [],
      ) ?? [],
    ) ?? '';

  // LE Business Traveller: when user types in Departure/Arrival Airport (every segment, every leg),
  // check "Price includes GST" only if all those values are in the Australian airports list
  useEffect(() => {
    if (!isBusinessTraveller) {
      return;
    }

    const shouldIncludeGst = areAllFlightAirportsGstApplicable(flightLegs);
    setValue('payment_metadata.price_includes_gst', shouldIncludeGst);
  }, [isBusinessTraveller, flightLegsAirportKey, flightLegs, setValue]);

  const handleOfferTypeChange = useCallback(
    (event: SelectChangeEvent<App.CustomOfferType>) => {
      const nextType = event.target.value;
      const currentInternalDiscountTracking = watch('payment_metadata.internal_discount_tracking');
      setValue('supplier', 'Other');
      setValue('item.vendor', '');
      setValue('item.type', nextType);
      changeFormType(nextType, currentInternalDiscountTracking);
    },
    [setValue, watch, changeFormType],
  );

  const handleGenerateTerms = useCallback(() => {
    const generatedTerms =
      brand === LE_BUSINESS_TRAVELLER
        ? generateLEBTFlightTermsAndConditions(flightTerms, dueDateValue)
        : generateFlightTermsAndConditions(flightTerms, dueDateValue);

    if (generatedTerms) {
      setValue('item.service_information', generatedTerms, { shouldValidate: true });
      enqueueSnackbar('Terms and conditions generated', { variant: 'success' });
    }
  }, [brand, flightTerms, dueDateValue, setValue, enqueueSnackbar]);

  const handleCostPriceCurrencyChange = useCallback(
    // cost price == vendor
    (name: string, value: string) => {
      if (contractFxRates) {
        const fieldName = name as keyof CustomOfferFormValues;
        const nextCostPriceCurrencyCode = value;

        const selectCostPriceFxRate = contractFxRates[nextCostPriceCurrencyCode];

        if (!selectCostPriceFxRate || selectCostPriceFxRate === 0) {
          enqueueSnackbar(`Exchange rate not found for ${nextCostPriceCurrencyCode}`, { variant: 'error' });

          return;
        }

        setValue('payment_metadata.vendor_exchange_rate', selectCostPriceFxRate ?? 0);

        if (nextCostPriceCurrencyCode === priceCurrencyCode) {
          // ensure same rate is used for equivalent currency codes
          setValue('payment_metadata.customer_exchange_rate', selectCostPriceFxRate ?? 0);
        }

        setValue(fieldName, nextCostPriceCurrencyCode);
      }
    },
    [priceCurrencyCode, contractFxRates, setValue, enqueueSnackbar],
  );

  const handlePriceCurrencyChange = useCallback(
    // customer
    (name: string, value: string) => {
      if (customerFxRates) {
        const fieldName = name as keyof CustomOfferFormValues;
        const priceCurrencyCode = value;
        const selectPriceFxRate = customerFxRates[priceCurrencyCode];

        if (!selectPriceFxRate || selectPriceFxRate === 0) {
          enqueueSnackbar(`Exchange rate not found for ${priceCurrencyCode}`, { variant: 'error' });

          return;
        }

        if (costPriceCurrencyCode === priceCurrencyCode) {
          // hardcode this to the same rate as vendor
          setValue('payment_metadata.customer_exchange_rate', vendorExchangeRate);
        } else {
          setValue('payment_metadata.customer_exchange_rate', selectPriceFxRate ?? 0);
        }

        setValue(fieldName, priceCurrencyCode);

        if (priceCurrencyCode === 'AUD') {
          setValue('payment_metadata.le_entity', LE_ENTITY_AUS);
        } else {
          setValue('payment_metadata.le_entity', LE_ENTITY_INTL);
        }
      }
    },
    [costPriceCurrencyCode, vendorExchangeRate, customerFxRates, setValue, enqueueSnackbar],
  );

  const autofillPostPurchaseOfferName = useCallback(
    (baseOfferName: string, nextPostPurchaseType = postPurchaseType) => {
      if (!baseOfferName) {
        return;
      }

      const nextOfferName = buildPostPurchaseOfferName(baseOfferName, nextPostPurchaseType);
      setValue('item.name', nextOfferName, { shouldDirty: true, shouldValidate: true });
    },
    [postPurchaseType, setValue],
  );

  const handleSabreParsedData = useCallback(
    (parsedData: SabreParsedData) => {
      const { pnr, name, passengers, segments, legs, flightType } = parsedData;

      setValue('item.vendor_reference_number', pnr, { shouldValidate: true });
      setValue('item.provider', 'sabre', { shouldValidate: true, shouldDirty: true });
      setValue('item.flight_type', flightType, { shouldValidate: true, shouldDirty: true });
      setValue('item.name', name, { shouldValidate: true });

      if (flightLegsSectionRef.current) {
        flightLegsSectionRef.current.clearAllLegs();

        legs.forEach((legSegments) => {
          const formattedSegments: Array<App.CustomOfferFlightSegment> = legSegments.map((seg) => {
            const depDate = dayjs(seg.departureDate, 'DD/MM/YYYY');
            const arrDate = dayjs(seg.arrivalDate, 'DD/MM/YYYY');

            return {
              // LEBT stores the carrier code, which its schema requires and its calendar invite prints.
              airline: isBusinessTraveller ? seg.airlineCode : seg.airline,
              flightNumber: isBusinessTraveller ? seg.flightNumberOnly : seg.flightNumber,
              bookingClass: seg.bookingClass?.trim() ?? '',
              departureAirport: seg.departureAirport,
              departureAirportName: seg.departureAirportName,
              arrivalAirport: seg.arrivalAirport,
              arrivalAirportName: seg.arrivalAirportName,
              departureDate: depDate.isValid() ? depDate.toISOString() : '',
              departureTime: seg.departureTime,
              arrivalDate: arrDate.isValid() ? arrDate.toISOString() : '',
              arrivalTime: seg.arrivalTime,
              airlinePnr: seg.airlinePnr || '',
            };
          });

          const newLeg: App.CustomOfferFlightLeg = {
            segments: formattedSegments,
          };

          flightLegsSectionRef.current?.addFlightLeg(newLeg);
        });
      }

      // Set Price includes GST from imported airports when LE Business Traveller (same rule as typing)
      if (isBusinessTraveller) {
        const importedLegsForGst = legs.map((legSegments) => ({
          segments: legSegments.map((seg) => ({
            departureAirport: seg.departureAirport,
            arrivalAirport: seg.arrivalAirport,
          })),
        }));
        const shouldIncludeGst = areAllFlightAirportsGstApplicable(importedLegsForGst);
        setValue('payment_metadata.price_includes_gst', shouldIncludeGst);
      }

      if (segments.length > 0) {
        const firstSegment = segments[0];
        const lastSegment = segments[segments.length - 1];

        // We still need to safe the start_date and end_date for the custom offer
        const startDate = dayjs(firstSegment.departureDate, 'DD/MM/YYYY');
        if (startDate.isValid()) {
          setValue('item.start_date', startDate.toISOString());
        }
        const endDate = dayjs(lastSegment.arrivalDate, 'DD/MM/YYYY');
        if (endDate.isValid()) {
          setValue('item.end_date', endDate.toISOString());
        }

        setValue('supplier', firstSegment.airline);
      }

      if (travellersSectionRef.current) {
        const nextTravellers = passengers.map((passenger) => {
          const passengerType = passenger.type;
          const matchedEmployee = isBusinessTraveller
            ? getEmployeeFromSabrePassenger(passenger, businessEmployees)
            : null;

          return {
            first_name: passenger.firstName,
            middle_name: passenger.middleName || '',
            last_name: passenger.lastName,
            type: passengerType,
            date_of_birth: null,
            fare_and_taxes: 0,
            ...(isBusinessTraveller && {
              employee: matchedEmployee,
              ...(matchedEmployee && { email: matchedEmployee.email }),
            }),
          };
        });

        travellersSectionRef.current.replaceTravellers(nextTravellers);
      }
    },
    [setValue, isBusinessTraveller, travellersSectionRef, businessEmployees],
  );

  const handlePNRSearch = useCallback(
    async (pnr: string) => {
      if (!pnr?.trim()) {
        return;
      }

      const trimmedPnr = pnr.trim();

      try {
        setPnrSearching(true);
        const response = await OrdersService.flightPNRSearch(trimmedPnr);
        if (!response.result || response.result.length === 0) {
          enqueueSnackbar('PNR not found', { variant: isSalesMode ? 'info' : 'warning' });
          return;
        }

        const order = response.result[0];
        const isOfflineFlight = order.offline_flight_items?.length > 0;
        const flightItem = isOfflineFlight ? order.offline_flight_items[0] : order.flight_items?.[0];

        if (!flightItem) {
          enqueueSnackbar('No flight items found for this PNR', { variant: isSalesMode ? 'info' : 'warning' });
          return;
        }

        const orderId = order.id_orders;
        const itemId = flightItem.id;

        const flightDetailsResponse = await OrdersService.getFlightDetailsByOrderAndItem(orderId, itemId);

        let matchedTravellers: Array<{ first_name: string; middle_name: string; last_name: string; type: string }> = [];

        if (isOfflineFlight) {
          const reservations = flightDetailsResponse as unknown as Array<{
            PNR?: string;
            travellers?: Array<{
              firstName: string;
              middleName: string;
              lastName: string;
              type: string;
              birth_date: string;
            }>;
          }>;
          const reservation = reservations.find((r) => r?.PNR?.toUpperCase() === trimmedPnr.toUpperCase());
          if (reservation?.travellers) {
            matchedTravellers = reservation.travellers.map((t) => ({
              first_name: t.firstName || '',
              middle_name: t.middleName || '',
              last_name: t.lastName || '',
              type: (t.type || 'adult').toLowerCase(),
              date_of_birth: t.birth_date,
            }));
          }
        } else if (flightDetailsResponse) {
          const details = flightDetailsResponse;

          if (details.pnr_id?.toUpperCase() === trimmedPnr.toUpperCase() && details.travellers) {
            matchedTravellers = details.travellers.map((t) => ({
              first_name: t.given_name || '',
              middle_name: t.middlename || t.middle_names || '',
              last_name: t.surname || '',
              type: (t.type || 'adult').toLowerCase(),
              date_of_birth: t.birth_date,
            }));
          }
        }

        if (matchedTravellers.length === 0) {
          enqueueSnackbar('PNR not found in flight details', { variant: isSalesMode ? 'info' : 'warning' });
          return;
        }

        setValue('item.vendor_reference_number', trimmedPnr, { shouldValidate: true });

        const fallbackOfferName = flightItem.name ?? '';

        const details = flightDetailsResponse;
        const bookingLegs = details?.booking_journey?.legs || [];
        const tripType = (details?.trip_type || '').toLowerCase();
        const bookingJourneyOfferName = getBookingJourneyOfferName(bookingLegs);

        const baseName = bookingJourneyOfferName || fallbackOfferName;
        if (baseName) {
          const offerName = `${baseName}${tripType ? ` (${tripType})` : ''}`.trim();
          autofillPostPurchaseOfferName(offerName);
        }

        if (!isOfflineFlight) {
          const primaryLegFareType = (bookingLegs[0]?.fareType || tripType || '').toLowerCase();

          if (bookingLegs.length > 2 || primaryLegFareType === 'multicity') {
            setValue('item.flight_type', 'multi-city', { shouldValidate: true });
          } else if (primaryLegFareType === 'return' || tripType.includes('return') || bookingLegs.length === 2) {
            setValue('item.flight_type', 'return', { shouldValidate: true });
          } else {
            setValue('item.flight_type', 'one-way', { shouldValidate: true });
          }

          const firstLegFlights = bookingLegs[0]?.flights || [];
          if (firstLegFlights.length > 0) {
            setValue('item.start_date', firstLegFlights[0].departingDate, { shouldValidate: true });
          }

          if (bookingLegs.length >= 2) {
            const lastLegFlights = bookingLegs[bookingLegs.length - 1]?.flights || [];
            if (lastLegFlights.length > 0) {
              setValue('item.end_date', lastLegFlights[0].departingDate, { shouldValidate: true });
            }
          }
        }

        if (travellersSectionRef.current) {
          const priceFields = !isSalesMode ? { airline_fee: 0, le_fee: 0 } : { fare_and_taxes: 0 };
          const nextTravellers = matchedTravellers.map(
            (traveller) =>
              ({
                ...traveller,
                date_of_birth: null,
                ...priceFields,
              }) as App.CustomOfferTraveller,
          );

          travellersSectionRef.current.replaceTravellers(nextTravellers);
        }

        enqueueSnackbar('Flight details populated from PNR', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`PNR search failed: ${error.message || 'Unknown error'}`, {
          variant: isSalesMode ? 'info' : 'error',
        });
      } finally {
        setPnrSearching(false);
      }
    },
    [setValue, enqueueSnackbar, isSalesMode, travellersSectionRef, autofillPostPurchaseOfferName],
  );

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} data-testid="custom-offer-form">
      {isBusinessTraveller && isSalesMode && (
        <SabreGDSImport onParsedData={handleSabreParsedData} isSubmitting={isSubmitting} />
      )}
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={4}>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Customer: {user.fullName}
          </Typography>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Email: {user.email}
          </Typography>
        </Stack>
      </Stack>
      <Box mt={2}>
        <Typography variant="h6">General offer information</Typography>
        <CustomOfferRequiredFieldsLegend />
      </Box>

      <Grid mt={2} columns={18} spacing={2} container>
        <Grid size={{ xs: 6, md: 2 }}>
          <Controller
            name="item.type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel required>Offer type</InputLabel>
                <Select
                  {...field}
                  label="Offer type"
                  disabled={isOfferTypeSelectDisabled}
                  onChange={(event) => {
                    field.onChange(event);
                    handleOfferTypeChange(event);
                  }}
                  data-testid="offer-type"
                >
                  {offerTypeSelectOptions.map((offerType) => (
                    <MenuItem key={offerType.value} value={offerType.value}>
                      {offerType.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Custom Offer Type</InputLabel>
            <Select
              value={purchaseType}
              label="Custom Offer Type"
              disabled={isSubmitting}
              data-testid="custom-offer-type"
              onChange={(e) => {
                const newPurchaseType = e.target.value;
                setPurchaseType(newPurchaseType);
                if (!isBusinessTraveller) {
                  setValue('payment_metadata.merchant_fee_enabled', newPurchaseType === PURCHASE_TYPE_SALES);
                }
                if (newPurchaseType === PURCHASE_TYPE_POST_PURCHASE) {
                  setValue('item.service_information', '', { shouldValidate: false });
                  setValue('item.travel_scope', undefined, { shouldValidate: false });
                }
                if (travellersSectionRef.current) {
                  const currentTravellers = watch('travellers') || [];
                  const nextTravellers = currentTravellers.map((t) => {
                    const priceFields =
                      newPurchaseType === 'post-purchase' ? { airline_fee: 0, le_fee: 0 } : { fare_and_taxes: 0 };

                    return {
                      ...t,
                      ...priceFields,
                    };
                  });

                  travellersSectionRef.current.replaceTravellers(nextTravellers);
                }
              }}
            >
              <MenuItem value={PURCHASE_TYPE_SALES}>Sales</MenuItem>
              <MenuItem value={PURCHASE_TYPE_POST_PURCHASE}>Post-Purchase</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Controller
            name="item.salesforce_case_number"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Zendesk ticket number"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                data-testid="salesforce-case-number"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, md: isSalesMode ? 3 : 5 }}>
          <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
            <Controller
              name="item.vendor_reference_number"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="PNR"
                  placeholder="e.g. AB12C3"
                  required
                  error={!!error}
                  disabled={isSubmitting || pnrSearching}
                  fullWidth
                  value={pnrValue}
                  helperText={error?.message ?? FLIGHT_PNR_HELPER_TEXT}
                  data-testid="supplier-booking-reference"
                  sx={{ flex: 1, minWidth: 0 }}
                  onChange={(e) => {
                    // Provider PNRs are conventionally uppercase; normalise as the agent types.
                    const normalisedPnr = e.target.value.toUpperCase();
                    setPnrValue(normalisedPnr);
                    setValue('item.vendor_reference_number', normalisedPnr, { shouldValidate: true });
                  }}
                />
              )}
            />
            {!isSalesMode && (
              <Button
                type="button"
                variant="contained"
                onClick={() => handlePNRSearch(pnrValue)}
                disabled={isSubmitting || pnrSearching || !pnrValue.trim()}
                data-testid="sync-pnr-data-button"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {pnrSearching ? 'Searching...' : 'Sync PNR Data'}
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Controller
            name="item.provider"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel required>Provider</InputLabel>
                <Select {...field} label="Provider" disabled={isSubmitting} data-testid="flight-provider">
                  {FLIGHT_PROVIDER_OPTIONS.map((providerOption) => (
                    <MenuItem key={providerOption.value} value={providerOption.value}>
                      {providerOption.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Controller
            name="sold_at"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                label="Sales date"
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting || hasOrders}
                fullWidth
                data-testid="sales-date"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Controller
            name="payment_metadata.sales_attribution"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Sales Attribution"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting || shouldLockLeLiveAttribution}
                select
                fullWidth
                InputLabelProps={{ shrink: !!field.value }}
                data-testid="sales-attribution"
              >
                {getSalesAttributionList(field.value).map((attributionType) => (
                  <MenuItem
                    key={attributionType.value}
                    value={attributionType.value}
                    disabled={!attributionType.enabled}
                  >
                    {attributionType.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Stack direction="row" spacing={1}>
            <Box sx={{ width: 120, flexShrink: 0 }}>
              <Controller
                name="customer_phone_prefix"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Autocomplete
                    {...field}
                    value={phonePrefixOptions.find((opt) => opt.value === value) ?? null}
                    onChange={(_, selected) => onChange(selected?.value ?? '')}
                    options={phonePrefixOptions}
                    getOptionLabel={(option) => `+${option.value}`}
                    renderOption={(props, option) => <li {...props}>{option.label}</li>}
                    filterOptions={(options, { inputValue }) => {
                      const input = inputValue.toLowerCase();
                      return options.filter((opt) => opt.label.toLowerCase().includes(input));
                    }}
                    slotProps={{ popper: { style: { minWidth: 280 }, placement: 'bottom-end' } }}
                    autoHighlight
                    disabled={isSubmitting}
                    renderInput={(params) => <TextField {...params} label="Prefix" />}
                    data-testid="customer-phone-prefix"
                  />
                )}
              />
            </Box>
            <Controller
              name="customer_phone_number"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Phone Number"
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  fullWidth
                  inputProps={{ maxLength: 15 }}
                  data-testid="customer-phone-number"
                />
              )}
            />
          </Stack>
        </Grid>
        {purchaseType === PURCHASE_TYPE_POST_PURCHASE && (
          <Grid size={{ xs: 12, md: 12 }}>
            <Controller
              name="post_purchase_type"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error}>
                  <InputLabel required>Post-Purchase Type</InputLabel>
                  <Select
                    {...field}
                    label="Post-Purchase Type"
                    onChange={(e) => {
                      const newPostPurchaseType = e.target.value;
                      field.onChange(e);
                      setPostPurchaseType(newPostPurchaseType);

                      const currentOfferName = getValues('item.name') ?? '';
                      if (currentOfferName) {
                        const baseName = stripPostPurchaseSuffix(currentOfferName);
                        const nextOfferName = buildPostPurchaseOfferName(baseName, newPostPurchaseType);
                        setValue('item.name', nextOfferName, { shouldDirty: true, shouldValidate: true });
                      }
                    }}
                  >
                    {POST_PURCHASE_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        )}
      </Grid>
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={isAdditionalMarginVisible ? 5 : 6}>
          <TextField
            {...register('item.name')}
            label={isBusinessTraveller ? 'Route summary' : 'Offer name'}
            required
            helperText={
              errors.item?.name
                ? errors.item.name.message
                : isBusinessTraveller
                  ? 'For example, Sydney > Melbourne. Visible on the customer portal and emails.'
                  : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            InputLabelProps={{ shrink: !!watch('item.name') }}
            error={!!errors.item?.name}
            disabled={isSubmitting}
            fullWidth
            data-testid="offer-name"
          />
        </Grid>
      </Grid>
      <Grid mt={2} columns={4} spacing={2} container>
        {isSalesMode && (
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="reason"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Reason for custom offer"
                  required
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  select
                  fullWidth
                  data-testid="reason-for-custom-offer"
                  SelectProps={{
                    // Keep the closed field on a single line: the guidance text is for choosing,
                    // not for reading back the choice.
                    renderValue: (value: unknown) => {
                      const selected = String(value ?? '');
                      return getFlightReasonsList(selected).find((option) => option.value === selected)?.label ?? '';
                    },
                    MenuProps: { PaperProps: { sx: { maxWidth: 460 } } },
                  }}
                >
                  {getFlightReasonsList(reason).map((reasonOption) => (
                    <MenuItem value={reasonOption.value} key={reasonOption.value}>
                      <Stack>
                        <Typography variant="body2">{reasonOption.label}</Typography>
                        {reasonOption.description && (
                          <Typography variant="caption" color="text.secondary" whiteSpace="normal">
                            {reasonOption.description}
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        )}
        {reason === 'other' && (
          <Grid size={2}>
            <Controller
              name="reasonOther"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Reason for custom offer - Other"
                  required={isReasonOtherTextRequired(reason)}
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  fullWidth
                  data-testid="reason-for-custom-offer-other"
                />
              )}
            />
          </Grid>
        )}
      </Grid>
      <Box mt={2}>
        <Typography variant="h6">Flight Type *</Typography>
      </Box>
      <Box mt={2}>
        <Controller
          name="item.flight_type"
          control={control}
          render={({ field }) => (
            <FormControl component="fieldset">
              <RadioGroup {...field} row data-testid="flight-type-radio-group">
                <FormControlLabel value="one-way" control={<Radio />} label="One-way" data-testid="one-way-radio" />
                <FormControlLabel value="return" control={<Radio />} label="Return" data-testid="return-radio" />
                <FormControlLabel
                  value="multi-city"
                  control={<Radio />}
                  label="Multi-city"
                  data-testid="multi-city-radio"
                />
              </RadioGroup>
            </FormControl>
          )}
        />
      </Box>
      {isSalesMode && (
        <Box mt={2}>
          <Controller
            name="item.travel_scope"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error} sx={{ maxWidth: { xs: '100%', md: 320 } }}>
                <InputLabel id="flight-travel-scope-label" required>
                  Domestic or International
                </InputLabel>
                <Select
                  {...field}
                  labelId="flight-travel-scope-label"
                  label="Domestic or International"
                  value={field.value ?? ''}
                  disabled={isSubmitting}
                  data-testid="flight-travel-scope"
                >
                  {FLIGHT_TRAVEL_SCOPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Box>
      )}
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.start_date"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                label="Departure date"
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting}
                fullWidth
                data-testid="start-date"
              />
            )}
          />
        </Grid>
        {(flightType === 'return' || flightType === 'multi-city') && (
          <Grid
            size={{
              xs: 3,
              md: 1,
            }}
          >
            <Controller
              name="item.end_date"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <DateWidget
                  {...field}
                  slotProps={{
                    textField: {
                      helperText: error?.message,
                      error: !!error,
                      required: isFlightEndDateRequired(flightType),
                      InputLabelProps: { shrink: true },
                    },
                  }}
                  disabled={isSubmitting}
                  label="Return date"
                  minDate={watch(`item.start_date`) ? newDate(watch(`item.start_date`)) : undefined}
                  data-testid="end-date"
                />
              )}
            />
          </Grid>
        )}
      </Grid>

      <Box mt={2}>
        <Box mt={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Box>
              <Typography variant="h6">Flight Legs</Typography>
              {isBusinessTraveller && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  * {getLegCountRule(flightType)?.message ?? 'At least one flight leg is required.'}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Each leg represents a portion of the trip (e.g., outbound, return). Add segments within a leg for
                connecting flights.
              </Typography>
            </Box>
            {!isBusinessTraveller && isSalesMode && (
              <SabreGDSImport onParsedData={handleSabreParsedData} isSubmitting={isSubmitting} />
            )}
          </Stack>
        </Box>
        <FlightLegsSection
          ref={flightLegsSectionRef}
          {...asFlightLegsForm({ control, register, errors })}
          isSubmitting={isSubmitting}
          isBusinessTraveller={isBusinessTraveller}
        />
      </Box>

      <Box mt={2}>
        <Typography variant="h6" gutterBottom>
          Terms and Conditions
        </Typography>
      </Box>

      {isSalesMode && (
        <Grid mt={2} columns={12} spacing={4} container>
          <Grid size={3}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Airline Currency
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Controller
                      name="item.flight_terms_and_conditions.currency"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <FormControl error={fieldState.invalid} fullWidth>
                          <Autocomplete
                            {...field}
                            value={value || 'AUD'}
                            onChange={(_, newValue) => onChange(newValue)}
                            options={airlineCurrencies}
                            autoHighlight
                            disableClearable
                            disabled={isSubmitting}
                            renderInput={(params) => <TextField {...params} label="Airline Currency" />}
                            data-testid="airline-terms-currency"
                          />
                          <FormHelperText>{fieldState.error?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Airline Change Fee
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={6}>
                    <TextField
                      {...register('item.flight_terms_and_conditions.change_fee')}
                      label="Change fee (per passenger)"
                      type="number"
                      slotProps={{
                        htmlInput: {
                          step: 0.01,
                        },
                      }}
                      fullWidth
                      disabled={isSubmitting || watch('item.flight_terms_and_conditions.is_changeable')}
                      value={watch('item.flight_terms_and_conditions.is_changeable') ? '' : undefined}
                      InputLabelProps={{ shrink: true }}
                      data-testid="airline-change-fee"
                    />
                  </Grid>
                  <Grid size={6}>
                    <Controller
                      name="item.flight_terms_and_conditions.is_changeable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue('item.flight_terms_and_conditions.change_fee', 0);
                              }}
                            />
                          }
                          label="Non-changeable"
                          data-testid="airline-is-changeable"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Airline Cancellation Fee
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={6}>
                    <TextField
                      {...register('item.flight_terms_and_conditions.cancellation_fee')}
                      label="Cancellation fee (per passenger)"
                      type="number"
                      slotProps={{
                        htmlInput: {
                          step: 0.01,
                        },
                      }}
                      fullWidth
                      disabled={isSubmitting || watch('item.flight_terms_and_conditions.is_cancellable')}
                      value={watch('item.flight_terms_and_conditions.is_cancellable') ? '' : undefined}
                      InputLabelProps={{ shrink: true }}
                      data-testid="airline-cancellation-fee"
                    />
                  </Grid>
                  <Grid size={6}>
                    <Controller
                      name="item.flight_terms_and_conditions.is_cancellable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue('item.flight_terms_and_conditions.cancellation_fee', 0);
                              }}
                            />
                          }
                          label="Non-refundable"
                          data-testid="airline-is-cancellable"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Grid>
          <Grid size={3}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  {isBusinessTraveller ? 'LE Business Traveller Currency' : 'Luxury Escapes Currency'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Controller
                      name="item.flight_terms_and_conditions.le_currency"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <FormControl error={fieldState.invalid} fullWidth>
                          <Autocomplete
                            {...field}
                            value={value || 'AUD'}
                            onChange={(_, newValue) => onChange(newValue)}
                            options={customerCurrencies}
                            autoHighlight
                            disableClearable
                            disabled={isSubmitting}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={isBusinessTraveller ? 'LEBT Service Fee Currency' : 'LE Service Fee Currency'}
                              />
                            )}
                            data-testid="le-terms-currency"
                          />
                          <FormHelperText>{fieldState.error?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  {isBusinessTraveller ? 'LEBT Change Fee' : 'LE Change Fee'}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={6}>
                    <TextField
                      {...register('item.flight_terms_and_conditions.le_change_fee')}
                      label={
                        isBusinessTraveller
                          ? 'LEBT service fee for changes (per passenger)'
                          : 'LE service fee for changes (per passenger)'
                      }
                      type="number"
                      slotProps={{
                        htmlInput: {
                          step: 0.01,
                        },
                      }}
                      fullWidth
                      disabled={isSubmitting || watch('item.flight_terms_and_conditions.le_is_changeable')}
                      value={watch('item.flight_terms_and_conditions.le_is_changeable') ? '' : undefined}
                      InputLabelProps={{ shrink: true }}
                      data-testid="le-change-fee"
                    />
                  </Grid>
                  <Grid size={6}>
                    <Controller
                      name="item.flight_terms_and_conditions.le_is_changeable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue('item.flight_terms_and_conditions.le_change_fee', 0);
                              }}
                            />
                          }
                          label="Non-changeable"
                          data-testid="le-is-changeable"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  {isBusinessTraveller ? 'LEBT Cancellation Fee' : 'LE Cancellation Fee'}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={6}>
                    <TextField
                      {...register('item.flight_terms_and_conditions.le_cancellation_fee')}
                      label={
                        isBusinessTraveller
                          ? 'LEBT service fee for cancellations (per passenger)'
                          : 'LE service fee for cancellations (per passenger)'
                      }
                      type="number"
                      slotProps={{
                        htmlInput: {
                          step: 0.01,
                        },
                      }}
                      fullWidth
                      disabled={isSubmitting || watch('item.flight_terms_and_conditions.le_is_cancellable')}
                      value={watch('item.flight_terms_and_conditions.le_is_cancellable') ? '' : undefined}
                      InputLabelProps={{ shrink: true }}
                      data-testid="le-cancellation-fee"
                    />
                  </Grid>
                  <Grid size={6}>
                    <Controller
                      name="item.flight_terms_and_conditions.le_is_cancellable"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setValue('item.flight_terms_and_conditions.le_cancellation_fee', 0);
                              }}
                            />
                          }
                          label="Non-refundable"
                          data-testid="le-is-cancellable"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      )}

      {isSalesMode && (
        <Box mt={2}>
          <Button
            variant="contained"
            onClick={handleGenerateTerms}
            disabled={isSubmitting}
            data-testid="generate-terms-button"
          >
            Generate Terms & Conditions
          </Button>
        </Box>
      )}

      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={6}>
          <RichTextEditor
            {...register('item.service_information')}
            control={control}
            label="Fare rule"
            required={isSalesMode}
            helperText={errors.item?.service_information?.message}
            error={!!errors.item?.service_information}
            disabled={isSubmitting}
            data-testid="terms-and-conditions"
          />
        </Grid>
      </Grid>
      <Box mt={3}>
        <Typography variant="h6">Passengers</Typography>
      </Box>
      <TravellersSection
        ref={travellersSectionRef}
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        isSubmitting={isSubmitting}
        isBusinessTraveller={isBusinessTraveller}
        isEditMode={isEditMode}
        businessEmployees={businessEmployees}
        customerHasPaid={customerHasPaid}
        lockVendorPriceFieldsForPaidOrder={lockVendorPriceFieldsForPaidOrder}
        itemType="flight"
        purchaseType={purchaseType}
      />

      {!isSalesMode && (
        <Box mt={2}>
          <Typography variant="h6">Payment Summary</Typography>
          <Grid mt={1} container columns={6} spacing={2}>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                label="Sum of Airline Fee (Cost Price)"
                value={computedCostPrice.toFixed(2)}
                disabled
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                data-testid="sum-airline-fee"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                label="Sum of LE Fee"
                value={computedLeFeeTotal.toFixed(2)}
                disabled
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                data-testid="sum-le-fee"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                label="Sell Price"
                value={computedSellPrice.toFixed(2)}
                disabled
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                data-testid="post-purchase-sell-price"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Box mt={2}>
        <Typography variant="h6">Customer Offer Price - {priceCurrencyCode}</Typography>
      </Box>
      <Grid mt={2} container columns={6} spacing={2}>
        {isSalesMode && (
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              {...register('payment_metadata.margin')}
              helperText={errors.payment_metadata?.margin?.message}
              error={!!errors.payment_metadata?.margin}
              disabled={isSubmitting || lockVendorPriceFieldsForPaidOrder}
              label="Margin"
              type="number"
              slotProps={{
                htmlInput: {
                  step: 0.01,
                },
              }}
              fullWidth
              data-testid="margin"
            />
          </Grid>
        )}
        <Grid size={{ xs: 3, md: 1 }}>
          <TextField
            {...register('cost_price')}
            helperText={errors.cost_price?.message}
            error={!!errors.cost_price}
            disabled
            label="Cost price (auto-calculated)"
            type="number"
            slotProps={{
              htmlInput: {
                step: 0.01,
              },
              inputLabel: {
                shrink: true,
              },
            }}
            fullWidth
            data-testid="cost-price"
          />
        </Grid>
        <Grid
          size={{
            xs: 3,
            md: 1,
          }}
        >
          <TextField
            {...register('price')}
            helperText={errors.price?.message}
            error={!!errors.price}
            disabled={isSubmitting || isEditMode}
            label="Sell price"
            type="number"
            slotProps={{
              htmlInput: {
                step: 0.01,
              },
              inputLabel: {
                shrink: true,
              },
            }}
            fullWidth
            data-testid="sell-price"
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="price_currency"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} fullWidth>
                <Autocomplete
                  {...field}
                  value={field.value || 'AUD'}
                  onChange={(_, data) => {
                    handlePriceCurrencyChange(field.name, data);
                  }}
                  options={customerCurrencies}
                  autoHighlight
                  disableClearable
                  renderInput={(params) => <TextField {...params} label="Customer Currency" />}
                  data-testid="customer-currency"
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="cost_price_currency"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} fullWidth>
                <Autocomplete
                  {...field}
                  value={field.value || 'AUD'}
                  options={costPriceCurrencies}
                  autoHighlight
                  disableClearable
                  disabled={isSubmitting || lockVendorPriceFieldsForPaidOrder}
                  renderInput={(params) => <TextField {...params} label="Vendor Currency" />}
                  onChange={(_, data) => {
                    handleCostPriceCurrencyChange(field.name, data);
                  }}
                  data-testid="vendor-cost-price-currency"
                />
                {fieldState.error?.message && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        {expectedCustomerCurrency && priceCurrencyCode !== expectedCustomerCurrency && (
          <Grid size={6}>
            <Alert severity="warning" data-testid="currency-mismatch-warning">
              Customer is based in {user.country_code} — expected currency is {expectedCustomerCurrency}, but{' '}
              {priceCurrencyCode} is selected. Please confirm this is intentional.
            </Alert>
          </Grid>
        )}
      </Grid>

      {isBusinessTraveller && (
        <CustomOfferBusinessTravellerServiceFeeSection
          userId={user.id_member}
          control={control}
          setValue={setValue}
          watch={watch}
          isSubmitting={isSubmitting}
          disabled={lockVendorPriceFieldsForPaidOrder}
        />
      )}

      <Grid mt={2} container columns={6} spacing={2}>
        {isSalesMode && !isBusinessTraveller && (
          <>
            <Grid size={{ xs: 3, md: 1 }}>
              <Controller
                name="payment_metadata.service_fee_type"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Service fee type"
                    helperText={error?.message}
                    error={!!error}
                    disabled={isSubmitting}
                    select
                    fullWidth
                    data-testid="service-fee-type"
                  >
                    {SERVICE_FEE_TYPE.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                {...register(`payment_metadata.le_service_fee`)}
                helperText={errors.payment_metadata?.le_service_fee?.message}
                error={!!errors.payment_metadata?.le_service_fee}
                disabled={isSubmitting || lockVendorPriceFieldsForPaidOrder}
                label="Service fee"
                type="number"
                slotProps={{
                  htmlInput: {
                    step: 0.01,
                  },
                }}
                fullWidth
                data-testid="service-fee"
              />
            </Grid>
          </>
        )}
        {(isBusinessTraveller || isSalesMode) && (
          <Grid size={{ xs: 3, md: 2 }}>
            <CustomOfferMerchantFeeField
              isBusinessTraveller={isBusinessTraveller}
              userId={user.id_member}
              offerId={offer?.id}
              control={control}
              setValue={setValue}
              hasOrders={hasOrders}
              isSubmitting={isSubmitting}
            />
          </Grid>
        )}
        {Number(watchedSellPrice) > 0 && (
          <Grid size={{ xs: 6, md: 2 }}>
            <Box>
              <CustomOfferLoyaltyCalculator
                productType="flight"
                sellPrice={Number(watchedSellPrice) || 0}
                currency={priceCurrencyCode}
                margin={Number(watchedMargin) || 0}
                leServiceFee={Number(watchedServiceFee) || 0}
                userId={user?.id_member}
                salesAttribution={salesAttribution}
                brand={brand}
                purchaseType={purchaseType}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      <CustomerPaymentsSection
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        paymentFlowV2Errors={paymentFlowV2Errors}
        hasOrders={hasOrders}
        isSubmitting={isSubmitting}
        isCustomOfferPackage={false}
        isBusinessTraveller={isBusinessTraveller}
        isEditMode={isEditMode}
        user={user}
        offer={offer}
        price={watchedSellPrice}
        customerDepositAmount={0}
        sectionState={customerPaymentSectionState}
        showLegacyPaymentFields={false}
        showMerchantFee={false}
        showManualDepositOption={showFlightManualDepositOption}
        showDepositMenuItem={false}
        paymentFlowHelperText={flightDepositPaymentFlowHelperText}
      />

      {isEditMode && (
        <Grid mt={2} container columns={6} spacing={2}>
          <CustomOfferInternalDiscountField control={control} errors={errors} isSubmitting={isSubmitting} isReadOnly />
        </Grid>
      )}
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={6}>
          <TextField
            {...register('notes')}
            helperText={errors.notes?.message}
            error={!!errors.notes}
            disabled={isSubmitting}
            label="Internal notes"
            minRows={2}
            fullWidth
            multiline
            data-testid="internal-notes"
          />
        </Grid>
      </Grid>
      <Box mt={2}>
        <ImagesForm
          images={images}
          hasHiddenToggle={false}
          showSaveButton={false}
          onAddImage={onAddImage}
          onUpdateImages={onUpdateImages}
          onDeleteImage={onDeleteImage}
        />
      </Box>
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={6}>
          <PDFUploadField
            control={control as unknown as Control<Record<string, unknown>>}
            name="pdf"
            label="PDF Document"
            error={errors.pdf as FieldError}
            disabled={isSubmitting}
            existingPdfData={{
              pdf_filename: offer?.pdf_filename,
              pdf_s3_key: offer?.pdf_s3_key,
            }}
          />
        </Grid>
      </Grid>
      {order?.id_orders && (
        <OrderDetailNotes
          orderId={order?.id_orders}
          customer={user}
          refreshData={() => {
            setTimeout(() => window.location.reload(), 200);
          }}
          offerId={offer?.id}
        />
      )}
      <CustomOfferGstWidget
        control={control}
        errors={errors}
        hasOrders={hasOrders}
        isSubmitting={isSubmitting}
        watch={watch}
        australiaGstHelperText={
          isBusinessTraveller
            ? 'GST only included if every departure and arrival airport for every segment of every leg is located in Australia'
            : undefined
        }
      />
      {isEditMode && (
        <Box mt={2}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inclusionsDiscussed}
                  onChange={(e) => setInclusionsDiscussed(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="inclusions-discussed"
                />
              }
              label="Inclusions discussed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsExplained}
                  onChange={(e) => setTermsExplained(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="terms-explained"
                />
              }
              label="Terms and Conditions explained"
            />
          </FormGroup>
        </Box>
      )}
      {!isCustomOfferPackage && (
        <Stack direction="row" spacing={2}>
          <FormGroup row>
            <Controller
              name="hiddenFromCustomer"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={isSubmitting}
                      data-testid="hidden-from-customer"
                    />
                  }
                  label="Hide from customer portal"
                />
              )}
            />
          </FormGroup>
          <CustomOfferLinkedPackageOfferField
            control={control}
            currentOffer={offer}
            userId={user.id_member}
            isSubmitting={isSubmitting}
          />
        </Stack>
      )}
      {isCustomOfferPackage && <CustomOfferPackageComponentsSection currentOffer={offer} userId={user.id_member} />}
      <Stack mt={2} direction="row" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={2}>
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={isActionsDisabled}
            data-testid="save-button"
          >
            Save
          </Button>

          {showSendButton && (
            <Button
              type="button"
              startIcon={<CheckIcon />}
              variant="contained"
              color="secondary"
              disabled={isSendDisabled}
              onClick={onSendPayment}
              data-testid="confirm-button"
            >
              Confirm and show in my escapes
            </Button>
          )}
        </Stack>
        {offer?.status === 'draft' && (
          <Stack direction="row" spacing={2}>
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={isActionsDisabled}
              onClick={openDeleteDialog}
              data-testid="delete-button"
            >
              Delete
            </Button>
          </Stack>
        )}
        <ConfirmDeleteDialog
          isOpen={deleteDialogOpen}
          description={offer?.name}
          handleClose={closeDeleteDialog}
          handleDelete={deleteOffer}
        />
      </Stack>
    </form>
  );
}
